import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Draft } from '@proofroom/schema';
import { Chip, formatDate } from '../components/ui';
import { stepZoom } from '../render/scale';
import { useViewer } from '../state/viewerStore';
import type { OptionGroup } from '../render/layers';
import { LayerSwitcher } from './LayerSwitcher';
import { previewLabel } from './previewLabel';
import type { ConnectionState } from '../api/events';
import { drafts as draftApi } from '../api/endpoints';
import { keys } from '../api/queries';
import { useSession } from '../auth/session';

const preview = previewLabel(__PR_AGREEMENT__);

export interface ToolbarProps {
  /** 'guest' keeps the page strip, zoom, reference, options and the Comment tool; everything that edits, decides, publishes or exports goes */
  mode?: 'user' | 'guest';
  scale: number;
  groups: OptionGroup[];
  drafts: Draft[];
  draft: Draft;
  documentId: string;
  connection: ConnectionState;
  engineOn: boolean;
  onEngineToggle(): void;
  dirtyCount: number;
  sending: boolean;
  onSend(): void;
  onPreview(): void;
}

export function Toolbar(p: ToolbarProps) {
  const v = useViewer();
  const [draftsOpen, setDraftsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!draftsOpen) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setDraftsOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [draftsOpen]);
  const pct = Math.round(p.scale * 100);

  const qc = useQueryClient();
  const { user } = useSession();
  const isGuest = p.mode === 'guest';
  const canApprove = !isGuest && (user?.role === 'client' || user?.role === 'admin');
  const onApproved = () => void qc.invalidateQueries({ queryKey: keys.document(p.documentId) });
  const approve = useMutation({ mutationFn: () => draftApi.approve(p.draft.id), onSuccess: onApproved });
  const unapprove = useMutation({ mutationFn: () => draftApi.unapprove(p.draft.id), onSuccess: onApproved });
  return (
    <div className="toolbar" role="toolbar" aria-label="Viewer tools">
      <div className="group rel" ref={ref}>
        {isGuest
          ? <span className="small muted">Draft {p.draft.number}{p.draft.label ? ` · ${p.draft.label}` : ''}</span>
          : <button className={`btn sm${draftsOpen ? ' on' : ''}`} onClick={() => setDraftsOpen((o) => !o)} data-tour="drafts">Draft {p.draft.number}{p.draft.label ? ` · ${p.draft.label}` : ''}</button>}
        {draftsOpen && !isGuest && (
          <div className="popover" style={{ minWidth: 320 }}>
            <h3>Drafts</h3>
            {[...p.drafts].sort((a, b) => b.number - a.number).map((d) => (
              <Link key={d.id} className="opt" to={`/documents/${p.documentId}/drafts/${d.id}`} onClick={() => setDraftsOpen(false)} style={{ fontWeight: d.id === p.draft.id ? 600 : 400 }}>
                <span style={{ width: 24 }}>{d.number}</span>
                <span style={{ flex: 1 }}>{d.label ?? `Draft ${d.number}`}</span>
                <span className="muted small">{formatDate(d.publishedAt ?? d.createdAt)} · {d.source}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className="divider" />
      <div className="group" aria-label="Zoom" data-tour="zoom">
        <button className="btn sm ghost" onClick={() => v.setZoom(stepZoom(p.scale, -1))} title="Zoom out (minus)">−</button>
        <span className="zoom">{pct}%</span>
        <button className="btn sm ghost" onClick={() => v.setZoom(stepZoom(p.scale, 1))} title="Zoom in (plus)">+</button>
        <button className={`btn sm${v.zoom === 'fit' ? ' on' : ' ghost'}`} onClick={() => v.setZoom('fit')} title="Fit spread (0)">Fit</button>
        <button className={`btn sm${v.zoom === 'fitWidth' ? ' on' : ' ghost'}`} onClick={() => v.setZoom('fitWidth')}>Width</button>
        <button className={`btn sm${v.zoom === 1 ? ' on' : ' ghost'}`} onClick={() => v.setZoom(1)} title="Actual size (1)">100%</button>
      </div>
      <div className="divider" />
      <div className="group">
        <button className={`btn sm${v.showReference ? ' on' : ''}`} onClick={() => v.toggleReference()} title="Overlay the full InDesign raster for comparison">Reference</button>
        {v.showReference && <input type="range" min={0} max={1} step={0.05} value={v.referenceOpacity} onChange={(e) => v.setReferenceOpacity(Number(e.target.value))} aria-label="Reference opacity" />}
        {!isGuest && <button className={`btn sm${v.showFrames ? ' on' : ''}`} onClick={() => v.toggleFrames()} title="Show text frame outlines">Frames</button>}
        {!isGuest && <button className={`btn sm${p.engineOn ? ' on' : ''}`} onClick={p.onEngineToggle} title="Recompose every story on this spread with the engine instead of showing InDesign's lines">Engine</button>}
        <LayerSwitcher groups={p.groups} overrides={v.layerOverrides} onChange={v.setLayerOverrides} />
      </div>
      <div className="divider" />
      <div className="group">
        <button className={`btn sm${v.tool === 'pin' ? ' on' : ''}`} onClick={() => v.setTool(v.tool === 'pin' ? 'select' : 'pin')} title="Add a comment anywhere on the page" data-tour="comment">Comment</button>
      </div>
      {canApprove && (
        <>
          <div className="divider" />
          <div className="group" data-tour="approve">
            {p.draft.approvedAt ? (
              <>
                <Chip kind="ok">Approved</Chip>
                <button className="btn sm ghost" disabled={unapprove.isPending} onClick={() => unapprove.mutate()}>{unapprove.isPending ? 'Withdrawing' : 'Unapprove'}</button>
              </>
            ) : (
              <button
                className="btn sm"
                disabled={approve.isPending}
                onClick={() => { if (window.confirm(`Approve draft ${p.draft.number} as final? You can withdraw approval later.`)) approve.mutate(); }}
              >
                {approve.isPending ? 'Approving' : 'Approve'}
              </button>
            )}
          </div>
        </>
      )}
      <span style={{ flex: 1 }} />
      <span className="badge preview-mark" title={preview.title}>{preview.short}</span>
      {!isGuest && (
        <>
          <span className="small muted" title="Live updates">
            <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, marginRight: 6, background: p.connection === 'open' ? 'var(--ok)' : p.connection === 'connecting' ? 'var(--warn)' : 'var(--danger)' }} />
            {p.connection === 'open' ? 'Live' : p.connection === 'connecting' ? 'Connecting' : 'Disconnected'}
          </span>
          <button className="btn sm ghost" onClick={p.onPreview} title="Export a PDF preview of this spread or the whole document" data-tour="preview">PDF preview</button>
          <button className="btn sm accent" disabled={p.dirtyCount === 0 || p.sending} onClick={p.onSend} title="Send every edited story to the designer" data-tour="send">
            {p.sending ? 'Sending' : p.dirtyCount > 0 ? `Send to designer (${p.dirtyCount})` : 'Send to designer'}
          </button>
        </>
      )}
    </div>
  );
}
